import { useRef, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const cardBaseSx = {
  borderRadius: 3,
  boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
  border: "1px solid #e2e8f0",
};

const onlyDigits = (value = "") => value.replace(/\D/g, "");

const formatCnpj = (value = "") => {
  const digits = onlyDigits(value).slice(0, 14);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatCep = (value = "") => {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatPhone = (value = "") => {
  const digits = onlyDigits(value).slice(0, 11);
  if (!digits) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export default function CompanyForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const lastCepRequestedRef = useRef("");
  const [formData, setFormData] = useState({
    // Dados da empresa
    name: "",
    trade_name: "",
    cnpj: "",
    state_registration: "",
    email: "",
    phone: "",
    city_code: "",
    zip_code: "",
    state: "",
    city: "",
    neighborhood: "",
    address: "",
    address_number: "",
    // Dados do supervisor
    supervisor_name: "",
    supervisor_email: "",
    supervisor_password: "",
  });

  const fillAddressByCep = async (cepDigits) => {
    if (cepDigits.length !== 8 || lastCepRequestedRef.current === cepDigits) {
      return;
    }

    lastCepRequestedRef.current = cepDigits;

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepDigits}/json/`);
      const viaCep = response.data;

      if (viaCep?.erro) return;

      setFormData((prev) => ({
        ...prev,
        address: viaCep.logradouro || prev.address,
        neighborhood: viaCep.bairro || prev.neighborhood,
        city: viaCep.localidade || prev.city,
        state: (viaCep.uf || prev.state || "").toUpperCase().slice(0, 2),
        city_code: onlyDigits(viaCep.ibge || prev.city_code).slice(0, 7),
      }));
    } catch (_error) {
      // Falha do ViaCEP não deve bloquear o preenchimento manual
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === "cnpj") finalValue = formatCnpj(value);
    if (name === "phone") finalValue = formatPhone(value);
    if (name === "zip_code") {
      const cepDigits = onlyDigits(value).slice(0, 8);
      finalValue = formatCep(value);

      if (cepDigits.length === 8) {
        void fillAddressByCep(cepDigits);
      } else {
        lastCepRequestedRef.current = "";
      }
    }
    if (name === "city_code") finalValue = onlyDigits(value).slice(0, 7);
    if (name === "state") finalValue = value.toUpperCase().slice(0, 2);

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/companies`, {
        ...formData,
        cnpj: onlyDigits(formData.cnpj),
        phone: onlyDigits(formData.phone),
        zip_code: onlyDigits(formData.zip_code),
        city_code: onlyDigits(formData.city_code),
      });
      toast.success("Empresa criada com sucesso!");
      navigate("/admin/companies");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao criar empresa"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box data-testid="company-create-page">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Nova Empresa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cadastre uma nova empresa no sistema
          </Typography>
        </Box>
        <Tooltip title="Voltar para lista" arrow>
          <IconButton
            onClick={() => navigate("/admin/companies")}
            size="small"
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Form */}
      <Card sx={cardBaseSx}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* === DADOS DA EMPRESA === */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Dados da Empresa
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: 3,
                mb: 4,
              }}
            >
              {/* 1ª linha: 4 campos alinhados */}
              <TextField
                fullWidth
                label="Nome da Empresa"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="CNPJ"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                placeholder="00.000.000/0000-00"
                inputProps={{ maxLength: 18 }}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Nome Fantasia"
                name="trade_name"
                value={formData.trade_name}
                onChange={handleChange}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Inscrição Estadual"
                name="state_registration"
                value={formData.state_registration}
                onChange={handleChange}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                inputProps={{ maxLength: 15 }}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Cód. Município IBGE"
                name="city_code"
                value={formData.city_code}
                onChange={handleChange}
                placeholder="Ex: 3550308"
                inputProps={{ maxLength: 7 }}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="CEP"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                placeholder="00000-000"
                inputProps={{ maxLength: 9 }}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="UF"
                name="state"
                value={formData.state}
                onChange={handleChange}
                inputProps={{ maxLength: 2 }}
                placeholder="SP"
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Cidade"
                name="city"
                value={formData.city}
                onChange={handleChange}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Bairro"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleChange}
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              <TextField
                fullWidth
                label="Número"
                name="address_number"
                value={formData.address_number}
                onChange={handleChange}
                placeholder="S/N"
                sx={{ gridColumn: { xs: "1 / -1", md: "span 1" } }}
              />

              {/* 2ª linha: endereço ocupando as 4 colunas */}
              <TextField
                fullWidth
                label="Endereço"
                name="address"
                value={formData.address}
                onChange={handleChange}
                multiline
                rows={2}
                sx={{ gridColumn: { xs: "1 / -1", md: "1 / span 4" } }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* === DADOS DO SUPERVISOR === */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Dados do Supervisor
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              mb={2}
            >
              O supervisor será criado automaticamente e poderá gerenciar os
              usuários da empresa
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(6, minmax(0, 1fr))",
                },
                gap: 3,
              }}
            >
              <TextField
                fullWidth
                label="Nome do Supervisor"
                name="supervisor_name"
                value={formData.supervisor_name}
                onChange={handleChange}
                required
                sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}
              />

              <TextField
                fullWidth
                label="Email do Supervisor"
                name="supervisor_email"
                type="email"
                value={formData.supervisor_email}
                onChange={handleChange}
                required
                sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}
              />

              <TextField
                fullWidth
                label="Senha do Supervisor"
                name="supervisor_password"
                type="password"
                value={formData.supervisor_password}
                onChange={handleChange}
                required
                helperText="Mínimo 6 caracteres"
                sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}
              />
            </Box>

            {/* Ações */}
            <Box display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SaveIcon />
                }
                disabled={loading}
              >
                {loading ? "Criando..." : "Criar Empresa"}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/admin/companies")}
                disabled={loading}
              >
                Cancelar
              </Button>
            </Box>

            <Box mt={3} p={2} bgcolor="info.lighter" borderRadius={1}>
              <Typography variant="body2" color="info.dark">
                ℹ️ A empresa será criada com{" "}
                <strong>15 dias de período de teste gratuito</strong>. Após esse
                período, será necessário realizar o pagamento mensal de{" "}
                <strong>R$ 90,00</strong>.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
